// Pure, testable helpers for the Game News panel. Consumes the shared backend
// contract EXACTLY (ingestion/dedupe/cache all live on the canonical backend —
// this project NEVER runs its own agents, history retrieval, or providers):
//   GET /api/v1/news?game_id&league&team&limit
//     -> { items:[{id,title,summary?,url,source,published_at,league,team_ids,game_ids}],
//          updated_at, status }  with status in live|cached|unavailable (UTC ISO times)
// Every field is read defensively so a missing/unknown field fails safe and a
// feed outage can never break room chat.

import { parseFlexibleDate, formatSnapshotTime, TIME_UNAVAILABLE } from "./datetime";

export const NEWS_STATUSES = ["live", "cached", "unavailable"];

// Only in-app-navigable? No — news links are external by design. Accept only
// absolute http(s) URLs for safe target=_blank links; anything else -> no link.
export function isSafeHttpUrl(url) {
  if (typeof url !== "string" || !url.trim()) return false;
  try {
    const u = new URL(url.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

// Human source host (e.g. "espn.com") for attribution when the source name is
// absent but a URL is present. Never throws.
export function sourceHost(url) {
  if (!isSafeHttpUrl(url)) return null;
  try { return new URL(url).host.replace(/^www\./, ""); } catch { return null; }
}

function firstDefined(...vals) {
  for (const v of vals) if (v !== undefined && v !== null && v !== "") return v;
  return null;
}

export function normalizeNewsItem(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const id = firstDefined(r.id, r._id, r.news_id, r.guid, r.url);
  const url = typeof r.url === "string" ? r.url.trim() : null;
  const safeUrl = isSafeHttpUrl(url) ? url : null;
  const source = firstDefined(r.source, r.source_name, r.publisher) || sourceHost(safeUrl) || "Unknown source";
  return {
    id: id != null ? String(id) : null,
    title: firstDefined(r.title, r.headline, r.name) || "",
    summary: firstDefined(r.summary, r.description, r.excerpt) || "",
    url: safeUrl,
    source,
    publishedAt: firstDefined(r.published_at, r.publishedAt, r.published, r.date, r.updated_at) || null,
    league: firstDefined(r.league) || null,
    teamIds: Array.isArray(r.team_ids) ? r.team_ids : (Array.isArray(r.teamIds) ? r.teamIds : []),
    gameIds: Array.isArray(r.game_ids) ? r.game_ids : (Array.isArray(r.gameIds) ? r.gameIds : []),
    raw: r,
  };
}

// Normalize the feed envelope. An absent/garbled payload -> status "unavailable"
// so the UI shows an honest unavailable state (NEVER "live"/"breaking").
export function normalizeNewsFeed(resp) {
  const r = resp && typeof resp === "object" ? resp : {};
  const rawStatus = String(r.status || "").toLowerCase().trim();
  const rawItems = Array.isArray(r.items) ? r.items : (Array.isArray(r.news) ? r.news : (Array.isArray(r) ? r : []));
  const items = rawItems.map(normalizeNewsItem).filter((n) => n.id != null && n.title);
  let status = NEWS_STATUSES.includes(rawStatus) ? rawStatus : (items.length ? "cached" : "unavailable");
  // An empty feed that the backend still calls live/cached is honestly "empty"
  // for display purposes but we keep the reported status for the chip.
  return {
    items,
    updatedAt: firstDefined(r.updated_at, r.updatedAt) || null,
    status,
  };
}

// Build the query params for GET /api/v1/news (omit empty keys so missing
// config fails fast on the backend side, not with bad params).
export function buildNewsQuery({ gameId, league, team, limit = 12 } = {}) {
  const q = {};
  if (gameId) q.game_id = gameId;
  if (league) q.league = league;
  if (team) q.team = team;
  if (limit) q.limit = limit;
  return q;
}

// Status chip copy — a cached feed is NEVER labelled live/breaking.
export function statusLabel(status) {
  switch (status) {
    case "live": return "Live";
    case "cached": return "Cached";
    default: return "Unavailable";
  }
}

export function publishedLabel(value) {
  return formatSnapshotTime(value);
}
export { TIME_UNAVAILABLE };

// Compose the room-chat message for a "Discuss" action: attributed headline +
// source + published timestamp + original-source link. Sent through the
// existing authorized room chat on an explicit click (no auto-flood).
export function buildDiscussMessage(item) {
  const n = item && typeof item === "object" ? item : {};
  const parts = [];
  if (n.title) parts.push(n.title);
  const meta = [];
  if (n.source) meta.push(n.source);
  const d = parseFlexibleDate(n.publishedAt);
  if (d) meta.push(formatSnapshotTime(n.publishedAt));
  if (meta.length) parts.push(meta.join(" · "));
  if (n.url) parts.push(n.url);
  return parts.join("\n");
}
